import { useContext, useEffect, useState, useCallback } from 'react';
import {
    Button,
    Card,
    CardBody,
    CardFooter,
    CardText,
    CardTitle,
    Row,
    Col,
    Alert,
} from 'reactstrap';
import { CampaignContext } from '../../../context/campaign';
import { useHistory } from 'react-router-dom';

import { getDashColor, renderIcon } from '../../../shared/components/icons';
import {
    IApplicationItem,
    IApplicationStatus,
    MyCampaignResponse,
} from '../../../types/campaign';
import { Link } from 'react-router-dom';
import {
    fetchFundRequestDashboardNotifications,
    fetchVendorChangeRequestNotifications,
    markVendorChangeRequestNotificationsAsRead,
} from '~/services/fundrequests';
import { RxCrossCircled } from 'react-icons/rx';

export interface ApplicationCardProps {
    app_item: IApplicationItem;
}

export interface ApplicationSectionProps {
    app_items: IApplicationItem[];
}

export interface ApplicationStatusProps {
    app_item: IApplicationStatus;
}

export interface ApplicationIncompleteSectionProps {
    status_item: IApplicationStatus;
    app_items: IApplicationItem[];
}

export interface AllCampaignProps {
    myCampaign: MyCampaignResponse;
}

export const ApplicationCard = ({ app_item }: ApplicationCardProps) => {
    let { name, title, status, complete, completed_date, extra, item_type } =
        app_item;
    const history = useHistory();

    // only display date if it is not empty - not all have dates required
    function get_status() {
        if (completed_date) {
            return status + ', ' + completed_date;
        }
        return status;
    }

    let path = '',
        pathState = {};

    if (item_type === 'contact') {
        path = '/team';
    } else if (item_type === 'profile') {
        path = '/patient/edit';
    } else if (item_type == 'medpro') {
        path = '/patient/medical-professional';
        if (complete) {
            pathState = { read_only: true };
        }
        if (status === 'Pending') {
            complete = true;
        }
    } else if (item_type == 'insurance') {
        path = '/patient/insurance';
        // if (complete) {
        //   pathState = { read_only: true };
        // }
    } else if (item_type == 'financial') {
        path = '/patient/finances';
        // if (complete) {
        //   pathState = { read_only: true };
        // }
    } else if (item_type == 'agreement') {
        path = '/patient/agreements';
    }

    return (
        <Card
            style={{
                height: '100%',
                borderWidth: complete ? '3px' : '',
                borderColor: complete
                    ? getDashColor('application_complete')
                    : '',
                boxShadow: complete ? '' : '0 12px 24px rgba(0, 0, 0, 0.25)',
                cursor: path ? 'pointer' : 'default',
            }}
            onClick={() => (path ? history.push(path, pathState) : {})}
        >
            <CardBody style={{ padding: '20px' }}>
                <div
                    style={{
                        marginBottom: '18px',
                        fontSize: '32px',
                        lineHeight: '1',
                    }}
                >
                    {renderIcon(status.toLowerCase())}
                </div>
                <CardTitle className='fw-bold mb-1'>{title}</CardTitle>
                <CardText className='mb-0'>{name}</CardText>
                <CardText className='mb-0' style={{ fontSize: '12px' }}>
                    {extra ? extra : ''}
                </CardText>
                <CardText className='mb-0' style={{ fontSize: '12px' }}>
                    {get_status()}
                </CardText>
            </CardBody>
            <CardFooter style={{ padding: '0 20px 20px 20px' }}>
                <Button
                    onClick={() => history.push(path, pathState)}
                    color='link'
                    className='p-0'
                >
                    {complete ? `View` : `Edit`}
                </Button>
            </CardFooter>
        </Card>
    );
};

export const ApplicationStatusSection = ({
    app_item,
}: ApplicationStatusProps) => {
    if (!app_item) {
        return <></>;
    }
    const [{ campaign }] = useContext(CampaignContext);
    const { extra } = app_item;
    const { campaignDetail } = campaign;

    // if (!app_item.has_application) {
    //   return <></>;
    // }

    const hiddenStatuses = ['disbursable', 'funded'];
    if (hiddenStatuses.includes(campaignDetail.campaign_status_code)) {
        return <></>;
    }

    return (
        <div>
            {/* {renderIcon(complete ? 'complete' : 'warning')} */}
            <div style={{ marginBottom: '4px' }}>
                <span
                    style={{
                        fontWeight: '900',
                        fontSize: '26px',
                        lineHeight: '32px',
                    }}
                >
                    Complete Application
                </span>
                <span className='text-muted' style={{ paddingLeft: '16px' }}>
                    {extra} Completed
                </span>
            </div>

            <p>
                In order for Help Hope Live to approve your campaign, please
                finish your Application. You are currently missing:
            </p>
        </div>
    );
};

export const ApplicationIncompleteSection = ({
    status_item,
    app_items,
}: ApplicationIncompleteSectionProps) => {
    if (!status_item || !app_items) {
        return <Card>undefined props</Card>;
    }
    const { has_application, complete } = status_item;

    const medProItem = app_items.find((item) => item.item_type === 'medpro');

    const completeAndNotPending = complete && medProItem?.status !== 'Pending';

    // only display if has application and application is not complete
    if (
        !has_application ||
        completeAndNotPending ||
        medProItem?.status === 'Unassigned'
    )
        return <></>;

    function showItems() {
        return (
            medProItem &&
            !medProItem.complete && (
                <CardText className='mb-2'>
                    <span style={{ fontSize: '28px', paddingRight: '14px' }}>
                        {renderIcon(
                            medProItem.complete ? 'complete' : 'pending'
                        )}
                    </span>
                    {medProItem.title}
                </CardText>
            )
        );
    }

    return (
        <Card
            style={{
                width: '100%',
                background: getDashColor('pending_verification'),
            }}
        >
            <CardBody>
                <CardTitle
                    style={{
                        fontWeight: '900',
                        fontSize: '26px',
                        lineHeight: '32px',
                    }}
                >
                    Pending Verification
                </CardTitle>
                {showItems()}
            </CardBody>
        </Card>
    );
};

export const ApplicationSection = (app_item: ApplicationSectionProps) => {
    if (!app_item) {
        return <></>;
    }
    // section that displays all the application items
    // if no items, then just returns
    const { app_items } = app_item;
    // if (app_items?.length == 0) {
    //   return <></>;
    // }

    return (
        <Row className='row-cols-2 row-cols-lg-3 row-cols-xl-6 g-3 g-md-4 pb-4'>
            {app_items?.map((item, i) => {
                return (
                    <Col key={i}>
                        <ApplicationCard app_item={item} />
                    </Col>
                );
            })}
        </Row>
    );
};

const itemPaths: { [key: string]: string } = {
    'Medical Professional': '/patient/medical-professional',
    Insurance: '/patient/insurance',
    Financial: '/patient/finances',
    Contact: '/team',
    Agreements: '/patient/agreements',
    'Client Information': '/patient/edit',
};

export const MessageCard = ({ app_item }: ApplicationStatusProps) => {
    const [{ showCampaignMessage }, campaignDispatch] =
        useContext(CampaignContext);

    let message =
        'In order for Help Hope Live to approve your campaign, please finish your Application. You are currently missing: ';

    if (app_item?.count === 0) {
        return null;
    }

    const itemsList = app_item?.items.map((item, index) => {
        const path = itemPaths[item] || '#';
        return (
            <div key={index}>
                <Link
                    to={path}
                    style={{ color: 'white', textDecoration: 'underline' }}
                >
                    {item}
                </Link>
            </div>
        );
    });

    const onDismiss = () => {
        campaignDispatch({
            type: 'set_show_campaign_message',
            payload: false,
        });
    };

    // Ensure visibility is explicitly checked to avoid rendering the alert before it's determined
    if (!showCampaignMessage) {
        return null;
    }

    return (
        <Alert
            isOpen={showCampaignMessage}
            style={{
                borderTopRightRadius: '0',
                borderBottomRightRadius: '0',
                backgroundColor: getDashColor('message'),
                borderColor: getDashColor('message'),
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25)',
                color: 'white',
            }}
        >
            <RxCrossCircled
                onClick={onDismiss}
                style={{
                    position: 'absolute',
                    top: '30px',
                    right: '25px',
                    fontSize: '1.5rem',
                    color: 'white',
                    cursor: 'pointer',
                }}
            />
            <div
                className='eyebrow'
                style={{
                    marginBottom: '18px',
                    color: 'white',
                }}
            >
                Message from Help Hope Live
            </div>
            <p>{message}</p>
            <div>{itemsList}</div>
        </Alert>
    );
};

type FundRequestDashboardNotification = {
    fund_request_id: number;
    note: string;
};

type VendorChangeRequestNotification = {
    vendor_change_request_id: number;
    vendor_name: string;
    status: string;
    date: string;
    reason: string | null;
};

export const DashboardNotifications = () => {
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [fundRequestNotifications, setFundRequestNotifications] = useState<
        FundRequestDashboardNotification[]
    >([]);
    const [vendorChangeNotifications, setVendorChangeNotifications] = useState<
        VendorChangeRequestNotification[]
    >([]);
    const [visible, setVisible] = useState(true);
    const [vendorNotificationsVisible, setVendorNotificationsVisible] =
        useState(true);

    const onDismiss = () => setVisible(false);

    const getDashboardNotifications = useCallback(async () => {
        try {
            const campaignId = campaign?.campaignDetail?.campaign_id;
            // Fetch fund request notifications
            const fundReqResp = await fetchFundRequestDashboardNotifications(
                campaignId
            );
            setFundRequestNotifications(fundReqResp);

            // Fetch vendor change request notifications
            const vendorChangeResp =
                await fetchVendorChangeRequestNotifications(campaignId);
            setVendorChangeNotifications(vendorChangeResp);
        } catch (error) {
            console.error('Error fetching dashboard notifications:', error);
        }
    }, [campaign]);

    const markVendorNotificationsAsRead = async () => {
        if (vendorChangeNotifications.length > 0) {
            const campaignId = campaign?.campaignDetail?.campaign_id;
            const notificationIds = vendorChangeNotifications.map(
                (notification) => notification.vendor_change_request_id
            );
            console.log('Marking notifications as read:', notificationIds);
            try {
                const response =
                    await markVendorChangeRequestNotificationsAsRead(
                        notificationIds,
                        campaignId
                    );
                console.log('Mark as read response:', response);
                return response;
            } catch (error) {
                console.error('Error marking notifications as read:', error);
                return false;
            }
        }
        return true;
    };

    useEffect(() => {
        if (campaign) {
            getDashboardNotifications();
        }
    }, [campaign, getDashboardNotifications]);

    // Handle vendor notification dismissal
    const handleVendorDismiss = async () => {
        const success = await markVendorNotificationsAsRead();
        if (success) {
            setVendorNotificationsVisible(false);
            // Force refresh notifications
            await getDashboardNotifications();
        }
    };

    // Handle regular notifications dismissal
    const handleDismiss = () => {
        setVisible(false);
    };

    const hasNotifications = fundRequestNotifications.length > 0;
    const hasVendorNotifications = vendorChangeNotifications.length > 0;

    return (
        <>
            {hasNotifications && visible && (
                <Alert
                    isOpen={visible}
                    style={{
                        borderTopRightRadius: '0',
                        borderBottomRightRadius: '0',
                        backgroundColor: getDashColor('message'),
                        borderColor: getDashColor('message'),
                        boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25)',
                        color: 'white',
                        marginBottom: hasVendorNotifications
                            ? '1rem'
                            : undefined,
                    }}
                >
                    <RxCrossCircled
                        onClick={handleDismiss}
                        style={{
                            position: 'absolute',
                            top: '30px',
                            right: '25px',
                            fontSize: '1.5rem',
                            color: 'white',
                            cursor: 'pointer',
                        }}
                    />
                    <div
                        className='eyebrow'
                        style={{
                            marginBottom: '18px',
                            color: 'white',
                        }}
                    >
                        Message from Help Hope Live
                    </div>
                    {fundRequestNotifications.length > 0 && (
                        <div>
                            <p>
                                One or more of your fund requests need your
                                attention.
                            </p>
                            <ul
                                style={{
                                    listStyleType: 'none',
                                    paddingLeft: 0,
                                }}
                            >
                                {fundRequestNotifications.map(
                                    (notification, index) => (
                                        <li key={index}>
                                            <Link
                                                to={`/requests/request/submit/${notification.fund_request_id}`}
                                                style={{
                                                    color: 'white',
                                                    textDecoration: 'underline',
                                                }}
                                            >
                                                View Fund Request #
                                                {notification.fund_request_id}
                                            </Link>
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>
                    )}
                </Alert>
            )}

            {hasVendorNotifications && vendorNotificationsVisible && (
                <Alert
                    isOpen={vendorNotificationsVisible}
                    style={{
                        borderTopRightRadius: '0',
                        borderBottomRightRadius: '0',
                        backgroundColor: getDashColor('message'),
                        borderColor: getDashColor('message'),
                        boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25)',
                        color: 'white',
                    }}
                >
                    <RxCrossCircled
                        onClick={handleVendorDismiss}
                        style={{
                            position: 'absolute',
                            top: '30px',
                            right: '25px',
                            fontSize: '1.5rem',
                            color: 'white',
                            cursor: 'pointer',
                        }}
                    />
                    <div
                        className='eyebrow'
                        style={{
                            marginBottom: '18px',
                            color: 'white',
                        }}
                    >
                        Payee Change Request Update
                    </div>
                    <div>
                        <p>
                            {vendorChangeNotifications.length > 1
                                ? 'You have payee change request updates'
                                : 'You have a payee change request update'}
                        </p>
                        <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                            {vendorChangeNotifications.map(
                                (notification, index) => (
                                    <li key={`vcr-${index}`}>
                                        <span>
                                            Payee "{notification.vendor_name}"
                                            change request was{' '}
                                            {notification.status}
                                            {notification.status ===
                                                'rejected' &&
                                                notification.reason &&
                                                `: ${notification.reason}`}
                                        </span>
                                    </li>
                                )
                            )}
                        </ul>
                    </div>
                </Alert>
            )}
        </>
    );
};
